import { SamplesDeliveryType } from '@caresend/types';
import { getRequiresCentrifuge, findEligibleNurseIDs } from './timeSlot.js';
import { deduplicateArray } from '../languageExtensions/array.js';
import { isNegative } from '../typeValidation/basicGuards.js';
import { isNullish } from '../typeValidation/nullish.js';

/** Calculates the nurse visit time separated by each component of the total time */

const calculateNurseVisitTimeComponents = visitTimeInfo => {
  const {
    additionalProcedureTime,
    firstProcedureTime,
    flexibleScheduling,
    numProcedures,
    nurseTransitMultiplier,
    placeGroupPricingMeta,
    transitTimePatientToPatient,
    transitTimeToDropOff,
    transitTimeToPatient
  } = visitTimeInfo;
  const patientTransitTime = transitTimeToPatient * nurseTransitMultiplier;
  let dropOffTransitTime = 0;
  /**
   * In the case that there is a mail-in request,
   * only consider the forecasted transit time to the mail-in waypoint once
   * and use the first result's multiplier for the forecast
   */

  const mailInRequest = placeGroupPricingMeta.find(placeGroup => placeGroup.deliveryType === SamplesDeliveryType.MAIL_IN);

  if (!isNullish(mailInRequest)) {
    const {
      placeGroupTransitMultiplier
    } = mailInRequest;
    dropOffTransitTime += transitTimeToDropOff * placeGroupTransitMultiplier;
  }
  /**
   * Iterate through the place groups that aren't of type `mailIn`
   * and add the forecasted transit time to the drop off transit time
   */


  placeGroupPricingMeta.filter(placeGroup => placeGroup.deliveryType !== SamplesDeliveryType.MAIL_IN).forEach(({
    placeGroupTransitMultiplier
  }) => {
    dropOffTransitTime += transitTimeToDropOff * placeGroupTransitMultiplier;
  });
  const {
    laborDensityMultiplier
  } = visitTimeInfo;
  const immutableTransitTime = flexibleScheduling ? transitTimePatientToPatient : 0;
  const transitTime = (patientTransitTime + dropOffTransitTime) * laborDensityMultiplier;
  const proceduresTime = firstProcedureTime + additionalProcedureTime * (numProcedures - 1);
  return {
    dropOffTransitTime,
    immutableTransitTime,
    patientTransitTime,
    proceduresTime,
    transitTime
  };
};
/** Calculates the Labor Cost of the nurses visit */

const calculateNurseVisitLaborCost = (laborCost, visitTimeInfo) => {
  const {
    proceduresTime,
    transitTime,
    immutableTransitTime
  } = calculateNurseVisitTimeComponents(visitTimeInfo);
  return (transitTime + immutableTransitTime + proceduresTime) / 60 * laborCost;
};
/** Calculates a visit's price */

const calculateVisitPrice = priceInfo => {
  const {
    caresendFee,
    groundShipmentEstimate,
    labShipmentCost,
    laborCost,
    numProcedures,
    patientShipmentCost,
    placeGroupPricingMeta,
    supplyProductionCost
  } = priceInfo;
  let totalCost = 0;
  /**
   * Base cost:
   * - Nurse labor cost
   * - Shipment cost to patient
   * Values are always added to the total
   */

  totalCost += laborCost;
  totalCost += patientShipmentCost;
  /**
   * Additional costs:
   * - CareSend fee
   * - Supply production cost
   * - Shipment cost to lab
   * Are only added to the total if the lab
   * does not pay for the sample processing
   *
   * - Ground shipment estimate
   * Is only subtracted from the total if the lab
   * pays for the sample processing
   */

  const labPaysForSamples = placeGroupPricingMeta.some(placeGroup => placeGroup.labSamplePayment);

  if (!labPaysForSamples) {
    totalCost += caresendFee;
    totalCost += supplyProductionCost * numProcedures;
    totalCost += labShipmentCost;
  } else {
    totalCost -= groundShipmentEstimate;
  }

  return Math.ceil(totalCost / 100) * 100;
};
/**
 * Calculate the price of a nurse visit for a given TimeSlot.
 */

const getNurseVisitPrice = (pricingInfo, timeSlotHelperParams, visitPlaceGroupIDs, nurseID, usePendingNurseID, isFallback = false) => {
  var _a;

  const isNullishOrNegative = val => isNullish(val) || isNegative(val);

  const {
    additionalProcedureTime,
    caresendFee,
    firstProcedureTime,
    groundShipmentEstimate,
    supplyProductionCost,
    shipmentCost,
    transitTimeToDropOff,
    transitTimeToPatient,
    transitTimePatientToPatient,
    flexiblePendingNurseTransitMultiplier
  } = pricingInfo;
  if (isNullishOrNegative(caresendFee) || isNullishOrNegative(supplyProductionCost) || isNullishOrNegative(firstProcedureTime) || isNullishOrNegative(groundShipmentEstimate) || isNullishOrNegative(additionalProcedureTime) || isNullishOrNegative(transitTimeToDropOff) || isNullishOrNegative(transitTimeToPatient) || isNullishOrNegative(transitTimePatientToPatient) || isNullishOrNegative(flexiblePendingNurseTransitMultiplier) || isNullishOrNegative(shipmentCost.modifier) || isNullishOrNegative(shipmentCost.procedureCount)) throw Error('Error getting CareSend pricing info');
  const {
    labShipmentInfo,
    patientShipmentInfo,
    placeGroups,
    procedures,
    timeSlot
  } = timeSlotHelperParams;
  const requiresCentrifuge = (_a = getRequiresCentrifuge(timeSlotHelperParams)) !== null && _a !== void 0 ? _a : undefined;
  let nurseOption;

  if (nurseID) {
    nurseOption = timeSlot.availableNurses[nurseID];
  } else {
    nurseOption = Object.values(findEligibleNurseIDs(timeSlotHelperParams, requiresCentrifuge, usePendingNurseID))[0];
  }

  if (!nurseOption) throw Error('No eligible nurse found for this time slot');
  /** Determine multipliers */

  const {
    laborCost
  } = nurseOption;
  let {
    laborDensityMultiplier,
    mailInDensityMultiplier
  } = timeSlot;
  const flexibleScheduling = !!timeSlotHelperParams.draftWaypoint.flexibleScheduling;
  const nurseTransitMultiplier = flexibleScheduling ? flexiblePendingNurseTransitMultiplier : nurseOption.transitMultiplier;
  laborDensityMultiplier = !flexibleScheduling || isNullish(laborDensityMultiplier) ? 1 : laborDensityMultiplier;
  mailInDensityMultiplier = !flexibleScheduling || isNullish(mailInDensityMultiplier) ? 1 : mailInDensityMultiplier;
  /** Total lab shipment cost across all mail-in place groups */

  let labShipmentCost = 0;
  /** Metadata regarding all unique place groups */

  const placeGroupPricingMeta = [];
  visitPlaceGroupIDs.forEach(visitPlaceGroupID => {
    var _a, _b, _c, _d, _e, _f, _g, _h;

    const placeGroupOptions = visitPlaceGroupID ? (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID] : undefined;
    const deliveryType = ((_b = placeGroupOptions === null || placeGroupOptions === void 0 ? void 0 : placeGroupOptions.walkIn) === null || _b === void 0 ? void 0 : _b.isOpen) && !isFallback ? SamplesDeliveryType.WALK_IN : SamplesDeliveryType.MAIL_IN;
    const placeGroupTransitMultiplier = (_d = (_c = placeGroupOptions === null || placeGroupOptions === void 0 ? void 0 : placeGroupOptions[deliveryType]) === null || _c === void 0 ? void 0 : _c.transitMultiplier) !== null && _d !== void 0 ? _d : 1;
    const labSamplePayment = (_f = (_e = placeGroups[visitPlaceGroupID]) === null || _e === void 0 ? void 0 : _e.labSamplePayment) !== null && _f !== void 0 ? _f : false;
    let placeGroupShipmentCost = 0;

    if (deliveryType == SamplesDeliveryType.MAIL_IN) {
      placeGroupShipmentCost = (_h = (_g = labShipmentInfo === null || labShipmentInfo === void 0 ? void 0 : labShipmentInfo[visitPlaceGroupID]) === null || _g === void 0 ? void 0 : _g.shipmentCost) !== null && _h !== void 0 ? _h : 0 * (mailInDensityMultiplier !== null && mailInDensityMultiplier !== void 0 ? mailInDensityMultiplier : 1);
      labShipmentCost += placeGroupShipmentCost;
    }

    placeGroupPricingMeta.push({
      id: visitPlaceGroupID,
      deliveryType,
      placeGroupTransitMultiplier,
      labSamplePayment,
      shipmentCost: placeGroupShipmentCost
    });
  });
  const numProcedures = procedures.length;
  const visitTimeInfo = {
    additionalProcedureTime,
    firstProcedureTime,
    flexibleScheduling,
    laborDensityMultiplier,
    numProcedures,
    nurseTransitMultiplier,
    placeGroupPricingMeta,
    transitTimePatientToPatient,
    transitTimeToDropOff,
    transitTimeToPatient
  };
  const visitTimeComponents = calculateNurseVisitTimeComponents(visitTimeInfo);
  const totalNurseLaborCost = calculateNurseVisitLaborCost(laborCost, visitTimeInfo);
  const patientShipmentCostModifier = numProcedures > shipmentCost.procedureCount ? shipmentCost.modifier : 1;
  const patientShipmentCost = patientShipmentCostModifier * patientShipmentInfo.shipmentCost;
  const visitPrice = calculateVisitPrice({
    caresendFee,
    groundShipmentEstimate,
    labShipmentCost,
    laborCost: totalNurseLaborCost,
    mailInDensityMultiplier,
    numProcedures,
    patientShipmentCost,
    placeGroupPricingMeta,
    supplyProductionCost
  });
  const deliveryTypes = deduplicateArray(placeGroupPricingMeta.map(placeGroup => placeGroup.deliveryType));
  const labSamplePayment = placeGroupPricingMeta.some(placeGroup => placeGroup.labSamplePayment);
  return {
    deliveryTypes,
    labShipmentCost,
    nurseID: nurseOption.nurseID,
    patientShipmentCarrier: patientShipmentInfo.carrier,
    patientShipmentCost,
    patientShipmentType: patientShipmentInfo.shipmentType,
    totalNurseLaborCost,
    labSamplePayment,
    visitPrice,
    patientShipmentCostModifier,
    placeGroupPricingMeta,
    visitTimeComponents
  };
};

export { calculateNurseVisitLaborCost, calculateNurseVisitTimeComponents, calculateVisitPrice, getNurseVisitPrice };
